import { validateOptionalEditorText } from "./editorValidation.js";

export type SpaceSlotEditorInput = {
  slotId: string;
  spaceId: string;
  label: string;
  labelAr: string;
  startsAt: string;
  endsAt: string;
  amountUsd: string;
};

export type SpaceSlotEditorDraft = {
  slotId: string;
  spaceId: string;
  label: string;
  labelAr: string;
  startsAt: Date;
  endsAt: Date;
  amountCents: number;
  currency: "usd";
  status: "available";
  source: "experience_five";
};

export type SpaceSlotEditorResult =
  | {
      ok: true;
      draft: SpaceSlotEditorDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export type SpaceSlotEditorFixture = {
  amountCents: number;
  endsAt: string;
  id: string;
  label: string;
  labelAr: string;
  spaceId: string;
  startsAt: string;
};

const idLimit = 80;
const spaceIdLimit = 120;
const labelLimit = 160;
const maxAmountCents = 100000;

export function buildSpaceSlotEditorInputFromFixture(
  fixture: SpaceSlotEditorFixture,
): SpaceSlotEditorInput {
  return {
    amountUsd: formatUsdInputValue(fixture.amountCents),
    endsAt: formatDateTimeLocal(fixture.endsAt),
    label: fixture.label,
    labelAr: fixture.labelAr,
    slotId: fixture.id,
    spaceId: fixture.spaceId,
    startsAt: formatDateTimeLocal(fixture.startsAt),
  };
}

export function buildDefaultSpaceSlotEditorInput(): SpaceSlotEditorInput {
  return {
    amountUsd: "150",
    endsAt: "2026-10-24T21:00",
    label: "Community hall evening reservation",
    labelAr: "حجز قاعة المجتمع المسائي",
    slotId: "community-hall-2026-10-24-evening",
    spaceId: "community-hall",
    startsAt: "2026-10-24T18:00",
  };
}

export function buildEmptySpaceSlotEditorInput(): SpaceSlotEditorInput {
  return {
    amountUsd: "",
    endsAt: "",
    label: "",
    labelAr: "",
    slotId: "",
    spaceId: "",
    startsAt: "",
  };
}

export function evaluateSpaceSlotEditorInput(input: SpaceSlotEditorInput): SpaceSlotEditorResult {
  const slotId = normalizeSpaceSlotDocumentId(input.slotId);
  const spaceId = normalizeSpaceSlotDocumentId(input.spaceId);
  const label = input.label.trim();
  const labelAr = input.labelAr.trim();
  const startsAt = parseDateTimeLocal(input.startsAt);
  const endsAt = parseDateTimeLocal(input.endsAt);
  const amountCents = parseUsdInputToCents(input.amountUsd);
  const errors: string[] = [];

  validateRequiredText("Space slot ID", slotId, idLimit, errors);
  validateRequiredText("Space ID", spaceId, spaceIdLimit, errors);
  validateRequiredText("English label", label, labelLimit, errors);
  validateOptionalEditorText("Arabic label", labelAr, labelLimit, errors);

  if (!startsAt) {
    errors.push("Starts at must be a valid date and time.");
  }

  if (!endsAt) {
    errors.push("Ends at must be a valid date and time.");
  }

  if (startsAt && endsAt && startsAt >= endsAt) {
    errors.push("Ends at must be after starts at.");
  }

  if (amountCents === undefined) {
    errors.push("Reservation amount must be a valid USD amount.");
  } else if (amountCents <= 0 || amountCents > maxAmountCents) {
    errors.push("Reservation amount must be greater than $0 and at most $1,000.");
  }

  if (errors.length > 0 || !startsAt || !endsAt || amountCents === undefined) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    draft: {
      amountCents,
      currency: "usd",
      endsAt,
      label,
      labelAr,
      slotId,
      source: "experience_five",
      spaceId,
      startsAt,
      status: "available",
    },
  };
}

export function normalizeSpaceSlotDocumentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, idLimit);
}

function formatDateTimeLocal(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 16);
}

function parseDateTimeLocal(value: string): Date | undefined {
  const trimmed = value.trim();

  if (!trimmed) {
    return undefined;
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function formatUsdInputValue(amountCents: number): string {
  if (amountCents % 100 === 0) {
    return String(amountCents / 100);
  }

  return (amountCents / 100).toFixed(2);
}

function parseUsdInputToCents(value: string): number | undefined {
  const trimmed = value.trim();

  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return undefined;
  }

  const parts = trimmed.split(".");
  const dollarsPart = parts[0];
  const centsPart = parts[1] ?? "";

  if (!dollarsPart) {
    return undefined;
  }

  const dollars = Number(dollarsPart);

  if (!Number.isSafeInteger(dollars)) {
    return undefined;
  }

  const cents = Number(centsPart.padEnd(2, "0"));
  const totalCents = dollars * 100 + cents;
  return Number.isSafeInteger(totalCents) ? totalCents : undefined;
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}
